import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;


  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();


    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    const infiniteEnabled = this.dataset.infinite !== 'false';
    this.#logDebug('connected', {
      sectionId: this.sectionId,
      href: window.location.href,
      infiniteEnabled,
    });
    if (infiniteEnabled) {
      this.#fetchPage('next');
      this.#fetchPage('previous');
      this.#observeViewMore();
    }

    // Intercept pagination link clicks to morph grid without full reload
    this.addEventListener('click', this.#onPaginationClick);
    window.addEventListener('popstate', this.#onPopState);

    // Sanitize any existing pagination links on first load
    this.#sanitizeNavLinks();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }


  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);

    this.removeEventListener('click', this.#onPaginationClick);
    window.removeEventListener('popstate', this.#onPopState);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;


    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;


    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);


    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) {
      this.#logDebug('skip-fetch-specific', { pageNumber, url: pageInfo.url?.toString() });
      return;
    }
    const effectiveUrl = pageInfo.url || new URL(window.location.href);
    this.#logDebug('fetch-specific', { pageNumber, url: effectiveUrl.toString() });
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, effectiveUrl);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');


    if (!nextPage || !this.#shouldUsePage(nextPage)) {
      this.#logDebug('skip-render-next', nextPage);
      return;
    }
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });


      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);


    this.#aspectRatioHelper.processNewElements();
    const safeNextUrl = this.#sanitizeURL(new URL(nextPage.url));
    history.pushState('', '', safeNextUrl.toString());
    await this.#refreshPagination(safeNextUrl);
    this.#logDebug('render-next', { page: nextPage.page, url: safeNextUrl.toString() });

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) {
      this.#logDebug('skip-render-prev', previousPage);
      return;
    }

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });


      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);


    this.#aspectRatioHelper.processNewElements();

    const safePrevUrl = this.#sanitizeURL(new URL(previousPage.url));
    history.pushState('', '', safePrevUrl.toString());
    this.#logDebug('render-prev', { page: previousPage.page, url: safePrevUrl.toString() });

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    await this.#refreshPagination(safePrevUrl);
    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**

   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;


    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    const result = {
      page,
      url,
    };
    this.#logDebug('compute-page', { type, currentCardPage, result: { page, url: url.toString() } });
    return result;
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;


    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;
    this.#logDebug('filter-update', { currentLastPage });

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };

  /**
   * Fetch latest section markup for the provided URL and replace the pagination nav
   * @param {URL} url
   */
  async #refreshPagination(url) {
    try {
      const sectionHTML = await sectionRenderer.getSectionHTML(this.sectionId, true, url);
      const parsed = new DOMParser().parseFromString(sectionHTML, 'text/html');
      const newNav = parsed.querySelector('nav.pagination');
      const currentNav = this.querySelector('nav.pagination');
      if (newNav && currentNav) {
        // Rewrite nav links to sanitized URLs (no section params)
        this.#sanitizeNavLinks(newNav);
        currentNav.replaceWith(newNav);
      }
    } catch (e) {
      this.#logDebug('refresh-pagination-error', e?.message || e);
    }
  }

  /**
   * Handle pagination link clicks
   * @param {MouseEvent} event
   */
  #onPaginationClick = async (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    // Find closest anchor inside nav.pagination
    const anchor = target.closest && target.closest('nav.pagination a');
    if (!(anchor instanceof HTMLAnchorElement)) return;

    // Only handle left click without modifiers
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    try {
      const url = this.#sanitizeURL(new URL(anchor.href, window.location.origin));
      await this.#replaceGridForURL(url);
      history.pushState('', '', url.toString());
    } catch (e) {
      this.#logDebug('pagination-click-error', e?.message || e);
      window.location.href = anchor.href; // fallback to full navigation
    }
  };

  /**
   * Handle browser back/forward
   */
  #onPopState = async () => {
    try {
      await this.#replaceGridForURL(new URL(window.location.href));
    } catch (e) {
      this.#logDebug('popstate-error', e?.message || e);
    }
  };

  /**
   * Replace the grid and pagination for a given URL using Section Rendering API
   * @param {URL} url
   */
  async #replaceGridForURL(url) {
    const sectionHTML = await sectionRenderer.getSectionHTML(this.sectionId, true, url);
    const parsed = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const newGrid = parsed.querySelector('[ref="grid"]');
    const newNav = parsed.querySelector('nav.pagination');
    const { grid } = this.refs;
    const currentNav = this.querySelector('nav.pagination');

    if (grid && newGrid) {
      // Replace list content entirely
      grid.replaceWith(newGrid);
      // Update refs after DOM swap
      this.refs.grid = /** @type {any} */ (newGrid);
    }

    if (currentNav && newNav) {
      this.#sanitizeNavLinks(newNav);
      currentNav.replaceWith(newNav);
    }

    // Clear cached pages since the base page changed
    this.pages.clear();
    // Preload neighbors again if infinite is enabled
    if (this.dataset.infinite !== 'false') {
      this.#fetchPage('next');
      this.#fetchPage('previous');
    }

    // Scroll to the top of results for better UX on mobile
    this.#scrollToResultsTop('smooth');
  }

  /**
   * Remove section rendering params from a URL so refresh loads a clean page
   * @param {URL} url
   * @returns {URL}
   */
  #sanitizeURL(url) {
    url.searchParams.delete('section_id');
    url.searchParams.delete('sections');
    url.searchParams.delete('sections_url');
    url.searchParams.delete('phcursor');
    return url;
  }

  /**
   * Scroll viewport to the start of the results list and set focus for a11y
   * @param {'smooth' | 'instant'} behavior
   */
  #scrollToResultsTop(behavior = 'smooth') {
    /** @type {HTMLElement | null} */
    const anchor = this.querySelector('#ResultsList') || document.getElementById('ResultsList');

    if (!anchor) {
      window.scrollTo({ top: 0, behavior });
      return;
    }

    const previousTabIndex = anchor.getAttribute('tabindex');
    anchor.setAttribute('tabindex', '-1');
    anchor.focus({ preventScroll: true });

    const top = anchor.getBoundingClientRect().top + window.scrollY - 8;
    window.scrollTo({ top, behavior });

    if (previousTabIndex === null) {
      anchor.removeAttribute('tabindex');
    }
  }

  /**
   * Sanitize pagination anchors within a container (or this element)
   * @param {ParentNode} [container]
   */
  #sanitizeNavLinks(container = this) {
    const anchors = Array.from(container.querySelectorAll('nav.pagination a'));
    for (const a of anchors) {
      if (a instanceof HTMLAnchorElement) {
        const safe = this.#sanitizeURL(new URL(a.href, window.location.origin));
        a.href = safe.toString();
      }
    }
  }

  /**
   * Debug logger gated by data-debug on the host element
   * @param {string} event
   * @param {any} payload
   */
  #logDebug(event, payload) {
    if (this.dataset.debug !== 'true') return;
    // eslint-disable-next-line no-console
    console.debug(`[pagination] ${event}`, payload);
  }
}
